import React from "react";
import theme from "assets/styles/themes";
import { HEADER_ZINDEX } from "constants/styles";
import { Box, SxProps, Theme, Typography, styled } from "@mui/material";

export type LayoutContentProps = {
    header: JSX.Element;
    title?: JSX.Element;
    body: JSX.Element;
};

export const HeaderInner = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: theme.spacing(4),
}));

export const TextHeader = ({ title }: { title: string }): JSX.Element => {
    return (
        <Typography sx={{ fontSize: "2.4rem", color: "common.black", fontWeight: 700 }}>
            {title.toUpperCase()}
        </Typography>
    );
};

export const Header = ({
    backgroundColor = "default",
    title,
    children,
    isSticky,
}: {
    backgroundColor?: keyof typeof backgroundColorOptions;
    title?: JSX.Element;
    children: React.ReactNode;
    isSticky?: boolean;
}): JSX.Element => {
    const backgroundColorOptions = {
        default: theme.palette.others.beige,
        primary: theme.palette.background.blue,
    };

    return (
        <Box
            sx={{
                top: 0,
                backgroundColor: backgroundColorOptions[backgroundColor],
                position: isSticky ? "sticky" : "static",
                zIndex: HEADER_ZINDEX,
            }}
        >
            <Box
                sx={(theme: Theme) => ({
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    padding: theme.spacing(4, 4, 0),
                })}
            >
                {title ? title : null}
                {children}
            </Box>
        </Box>
    );
};

export const Body = React.forwardRef(function Body(
    { children, sx }: { children: React.ReactNode; sx?: SxProps<Theme> },
    ref
) {
    return (
        <Box sx={sx} ref={ref}>
            {children}
        </Box>
    );
});

const LayoutContent = ({ header, title, body }: LayoutContentProps): JSX.Element => {
    return (
        <>
            <Header title={title}>{header}</Header>
            <Body>{body}</Body>
        </>
    );
};

export default LayoutContent;
